/**
 * @file /study/:familyId/sources/:sourceId/index — 資料のさくいん (語 → ページ)。
 *
 * @what: 資料の索引 (GET .../sources/{sourceId}/index) の語を五十音順で全て並べ、入力した
 *   語で絞り込み、そのページをリーダーで開く。語が多い資料はページ送りで見る。同じ語が
 *   複数のページを指すときは1行ずつ並ぶ。
 * @why: 巻末索引や対応表を、資料ごとに React を書かずに引けるようにする (本だなの資料に
 *   索引があれば、この面が使える)。原本を別の切り口で引く読みものは、資料ごとの内容
 *   (年表・一覧表) として content-as-code のまま置く (代表 2026-08-27)。
 * @failure: 所属外 familyId は拒否。索引が無い資料は案内のみ。取得失敗は 1 行で知らせる。
 */

import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { familyLabel, roleLabel } from "@/features/study/api/families";
import { useMyFamilies } from "@/features/study/hooks/useMyFamilies";
import { useSourceIndexes } from "@/features/study/hooks/useSourceIndexes";
import { useLearnerSelection } from "@/features/study/hooks/useLearnerSelection";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import { useStudySources } from "@/features/study/hooks/useStudySources";
import { searchIndexEntries, sortIndexEntries } from "@/features/study/model/sourceIndexSearch";
import { useHeaderFamily } from "@/shared/ui/headerContext";
import "@/features/study/components/study.css";

/** 1ページに何語まで並べるかを決める。 */
const PAGE_SIZE = 200;

/** 入力欄の例に何語出すかを決める。 */
const EXAMPLE_COUNT = 3;

/**
 * さくいんの面のルート。
 *
 * @what: 家族の所属を確かめてから IndexPage を描く (家族名はヘッダーへ渡す)。
 * @why: /study 配下の他のルート (リーダー・補助ページ) と同じ入り方に揃える。
 * @failure: 読み込み中は 1 行、所属外・取得失敗は家族の選び直しへ案内する。
 */
export function StudySourceIndexRoute() {
  const { familyId = "", sourceId = "" } = useParams();
  const state = useMyFamilies();
  useStudyBackground();
  // さくいんはまなぶ人によらないが、だれを扱っているかはどの面でもヘッダーに出す。
  useLearnerSelection(familyId);
  const family =
    state.status === "ready" ? state.families.find((f) => f.family_id === familyId) : undefined;
  useHeaderFamily(
    family
      ? {
          label: familyLabel(family),
          role: roleLabel(family.role),
          switchable: state.status === "ready" && state.families.length > 1,
        }
      : null,
  );

  if (state.status === "loading") {
    return (
      <div
        className="study-scope"
        role="status"
        aria-live="polite"
        style={{ padding: "var(--space-5)" }}
      >
        読み込み中...
      </div>
    );
  }
  if (state.status === "error" || !family) {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" className="study-muted">
          この家族には所属していないか、見つかりません。
        </p>
        <Link to="/study">家族を選び直す</Link>
      </section>
    );
  }
  return <IndexPage familyId={familyId} sourceId={sourceId} />;
}

/**
 * さくいんの本体。
 *
 * @what: 資料の題と索引を読み、語を入れていないときは全ての語を五十音順で、入れたら
 *   前方一致 → 部分一致の順で並べ、リーダーの ?page= へのリンクにする。1度に出すのは
 *   PAGE_SIZE 語までで、残りはページ送りで見る。
 * @why: 候補が見えないと何を入れればよいか分からないため、全ての語を並べたうえで入力に
 *   応じて絞り込む。数千語の資料でも一度に描かないよう、ページ送りで区切る。
 * @failure: 索引の読み込み中は1行だけ出す。空 (索引なし) のときは取り込みを案内する。
 *   資料の題が読めなければ「取り込んだ資料」と出して探す操作は止めない。
 */
function IndexPage({ familyId, sourceId }: { familyId: string; sourceId: string }) {
  const sourcesState = useStudySources(familyId);
  const ids = useMemo(() => [sourceId], [sourceId]);
  const indexes = useSourceIndexes(familyId, ids);
  const entries = indexes.get(sourceId);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const listTopRef = useRef<HTMLDivElement>(null);
  const source =
    sourcesState.status === "ready"
      ? (sourcesState.sources.find((s) => s.id === sourceId) ?? null)
      : null;
  const readerHref = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}`;

  const sorted = useMemo(() => sortIndexEntries(entries ?? []), [entries]);
  const hits = useMemo(() => searchIndexEntries(entries ?? [], query), [entries, query]);
  // 入力欄の例 = 五十音順に等間隔で拾った語 (毎回同じ顔ぶれになる)。
  const examples = useMemo(() => {
    if (sorted.length === 0) return [];
    const step = Math.max(1, Math.floor(sorted.length / EXAMPLE_COUNT));
    const out: string[] = [];
    for (let i = 0; i < sorted.length && out.length < EXAMPLE_COUNT; i += step) {
      const t = sorted[i]!.term;
      if (!out.includes(t)) out.push(t);
    }
    return out;
  }, [sorted]);

  const pageCount = Math.max(1, Math.ceil(hits.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const start = current * PAGE_SIZE;
  const shown = hits.slice(start, start + PAGE_SIZE);
  const searching = query.trim() !== "";
  const goTo = (next: number) => {
    setPage(Math.max(0, Math.min(pageCount - 1, next)));
    listTopRef.current?.scrollIntoView?.({ block: "start" });
  };

  return (
    <section className="study-scope study-page">
      <p className="study-aid-back">
        <Link to={readerHref}>← 本のページに戻る</Link>
      </p>
      <h1 className="study-aid-title">さくいん</h1>
      <p className="study-aid-tagline">{source?.title ?? "取り込んだ資料"}</p>
      {entries === undefined ? (
        <p role="status" className="study-muted">
          さくいんを読み込んでいます...
        </p>
      ) : entries.length === 0 ? (
        <p className="study-muted">
          この資料にはさくいんがありません。巻末の索引を取り込むと、ここで語から引けます。
        </p>
      ) : (
        <div className="study-aid-index" ref={listTopRef}>
          <label className="study-aid-search">
            <span className="study-aid-search-label">語をさがす</span>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={examples.join(" / ")}
              className="study-aid-search-input"
            />
          </label>
          {hits.length === 0 ? (
            <p className="study-muted">
              見つかりませんでした。ちがう言い方でさがしてみてください。
            </p>
          ) : (
            <>
              <p className="study-aid-note">
                {searching
                  ? `「${query.trim()}」に当たるのは${hits.length}語です。`
                  : `全部で${sorted.length}語あります。語の一部を入れると、その語だけになります。`}
              </p>
              {pageCount > 1 && (
                <nav className="study-aid-pager" aria-label="ページ送り">
                  <button
                    type="button"
                    className="study-aid-pager-button"
                    onClick={() => goTo(current - 1)}
                    disabled={current === 0}
                  >
                    ← 前へ
                  </button>
                  <span className="study-aid-pager-status">
                    {`${start + 1}〜${start + shown.length}語目 / 全${hits.length}語`}
                  </span>
                  <button
                    type="button"
                    className="study-aid-pager-button"
                    onClick={() => goTo(current + 1)}
                    disabled={current >= pageCount - 1}
                  >
                    次へ →
                  </button>
                </nav>
              )}
              <ul className="study-aid-index-list">
                {shown.map((e) => (
                  <li key={`${e.term}-${e.page}`}>
                    <Link to={`${readerHref}?page=${e.page}`} className="study-aid-index-entry">
                      <span className="study-aid-index-term">{e.term}</span>
                      <span className="study-aid-index-page">p.{e.page}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
